import { observable, action, makeObservable } from 'mobx';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

function getStoredTheme(): Theme | null {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === 'light' || value === 'dark' ? value : null;
}

function getSystemTheme(): Theme {
    return window.matchMedia && window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light';
}

function applyThemeToDom(theme: Theme) {
    document.documentElement.setAttribute('data-theme', theme);
}

class ThemeStore {
    @observable theme: Theme;
    private _mediaQuery: MediaQueryList;

    constructor() {
        makeObservable(this);
        this._mediaQuery = window.matchMedia(DARK_QUERY);
        this.theme = getStoredTheme() ?? getSystemTheme();
        applyThemeToDom(this.theme);

        this._mediaQuery.addEventListener('change', this._onSystemThemeChange);
        window.addEventListener('storage', this._onStorageEvent);
    }

    get isManual(): boolean {
        return getStoredTheme() !== null;
    }

    @action.bound
    toggleTheme() {
        this._setTheme(this.theme === 'dark' ? 'light' : 'dark', true);
    }

    @action.bound
    private _onSystemThemeChange(event: MediaQueryListEvent) {
        if (this.isManual) return;
        this._setTheme(event.matches ? 'dark' : 'light', false);
    }

    @action.bound
    private _onStorageEvent(event: StorageEvent) {
        if (event.key !== STORAGE_KEY) return;
        const stored = getStoredTheme();
        this._setTheme(stored ?? getSystemTheme(), false);
    }

    @action.bound
    private _setTheme(theme: Theme, persist: boolean) {
        this.theme = theme;
        applyThemeToDom(theme);
        if (persist) localStorage.setItem(STORAGE_KEY, theme);
    }
}

export const themeStore = new ThemeStore();
